// src/pages/domiciliario/DomiciliarioPanel.js
//
// Panel principal del domiciliario:
// - Lista de pedidos asignados (activos) con polling 10s.
// - Detalle con transiciones de estado (en_ruta → entregado).
// - Botón SOS georreferenciado.
// - Perfil rápido.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Stack, Card, CardContent, CardActionArea, Chip, Alert,
  CircularProgress, Divider, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, AppBar, Toolbar, Tab, Tabs, Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import WarningIcon       from '@mui/icons-material/Warning';
import RefreshIcon       from '@mui/icons-material/Refresh';
import LogoutIcon        from '@mui/icons-material/Logout';
import PersonIcon        from '@mui/icons-material/Person';
import PlaceIcon         from '@mui/icons-material/Place';
import DirectionsIcon    from '@mui/icons-material/Directions';
import PhoneIcon         from '@mui/icons-material/Phone';

import { pedidos as apiPedidos } from '../../api/pedidosApi';
import { useApp } from '../../context/AppContext';
import Logo from '../../components/Logo';
import { formatoCOP } from '../../utils/format';

const ESTADO_COLOR = {
  preparacion: 'info', en_ruta: 'warning', entregado: 'success', cancelado: 'error',
};
const ESTADO_LABEL = {
  preparacion: 'Esperando en tienda', en_ruta: 'En ruta',
  entregado: 'Entregado', cancelado: 'Cancelado',
};

export default function DomiciliarioPanel() {
  const { sesionBackend, cerrarSesionBackend } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [items, setItems]       = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');
  const [detalle, setDetalle]   = useState(null);
  const [sos, setSos]           = useState(null);

  const cargar = useCallback(async () => {
    setError('');
    try {
      const r = await apiPedidos.listar({ pageSize: 50 });
      const list = Array.isArray(r) ? r : (r.items || r.Items || []);
      setItems(list);
    } catch (e) { setError(e.mensajeUsuario || 'No se pudieron cargar los pedidos.'); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => {
    cargar();
    const id = setInterval(cargar, 10000);
    return () => clearInterval(id);
  }, [cargar]);

  const salir = async () => {
    await cerrarSesionBackend();
    navigate('/login', { replace: true });
  };

  const cambiarEstado = async (pedidoId, nuevoEstado) => {
    try {
      await apiPedidos.cambiarEstado(pedidoId, nuevoEstado);
      cargar();
      if (detalle) {
        const d = await apiPedidos.detalle(pedidoId);
        setDetalle(d);
      }
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo actualizar.'); }
  };

  const dispararSOS = async () => {
    try {
      const coords = await new Promise((res, rej) => {
        if (!navigator.geolocation) { rej(new Error('No hay GPS')); return; }
        navigator.geolocation.getCurrentPosition(
          (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
          rej,
          { enableHighAccuracy: true, timeout: 15000 }
        );
      }).catch(() => ({ lat: null, lng: null }));

      await apiPedidos.sos(sos.pedidoId, {
        mensaje:  sos.mensaje || null,
        latitud:  coords.lat,
        longitud: coords.lng,
      });
      setSos(null);
      alert('Alerta enviada. El negocio ya fue avisado.');
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo enviar la alerta.'); }
  };

  const activos      = items.filter(p => ['preparacion','en_ruta'].includes(p.estado || p.Estado));
  const historicos   = items.filter(p => ['entregado','cancelado'].includes(p.estado || p.Estado));
  const paraMostrar  = tab === 0 ? activos : historicos;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FBF7F0' }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Logo />
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <LocalShippingIcon fontSize="small" />
            </Avatar>
            <Typography variant="body2">{sesionBackend?.nombre?.split(' ')[0] || 'Domi'}</Typography>
          </Box>
          <IconButton onClick={salir} title="Cerrar sesión">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="overline" color="text.secondary">Mis pedidos</Typography>
              <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Pedidos asignados</Typography>
            </Box>
            <Button startIcon={<RefreshIcon />} onClick={cargar}>Actualizar</Button>
          </Box>

          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

          <Card>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label={`Activos (${activos.length})`} />
              <Tab label="Historial" />
            </Tabs>
          </Card>

          {cargando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : paraMostrar.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 5 }}>
                <Typography color="text.secondary">
                  {tab === 0 ? 'No tienes pedidos asignados en este momento.' : 'Aún no hay historial.'}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={2}>
              {paraMostrar.map((p) => {
                const pid = p.pedidoId || p.PedidoId;
                const est = p.estado || p.Estado;
                return (
                  <Card key={pid}>
                    <CardActionArea onClick={async () => {
                      try { setDetalle(await apiPedidos.detalle(pid)); }
                      catch (e) { setError(e.mensajeUsuario || 'No se pudo cargar.'); }
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="overline" color="text.secondary">{p.numeroPedido || p.NumeroPedido}</Typography>
                            <Typography variant="h6" sx={{ fontFamily: 'Fraunces' }}>{p.negocioNombre || p.NegocioNombre}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Para: {p.veciNombre || p.VeciNombre}
                            </Typography>
                          </Box>
                          <Chip label={ESTADO_LABEL[est] || est} color={ESTADO_COLOR[est] || 'default'} />
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="body2">
                            {p.direccionEntrega || p.DireccionEntrega}
                          </Typography>
                          <Typography variant="h6" sx={{ color: 'primary.main' }}>
                            {formatoCOP(p.total || p.Total)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Dialog detalle */}
      <Dialog open={!!detalle} onClose={() => setDetalle(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Typography variant="overline" color="text.secondary">{detalle?.numeroPedido || detalle?.NumeroPedido}</Typography>
          <Typography variant="h5" sx={{ fontFamily: 'Fraunces' }}>{detalle?.negocioNombre || detalle?.NegocioNombre}</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {detalle && (
            <Stack spacing={2}>
              <Chip
                label={ESTADO_LABEL[detalle.estado || detalle.Estado]}
                color={ESTADO_COLOR[detalle.estado || detalle.Estado] || 'default'}
                sx={{ alignSelf: 'flex-start' }}
              />

              <Box>
                <Typography variant="overline" color="text.secondary">
                  <PersonIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Cliente
                </Typography>
                <Typography variant="body1" fontWeight={600}>{detalle.veciNombre || detalle.VeciNombre}</Typography>
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary">
                  <PlaceIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Entrega
                </Typography>
                <Typography variant="body2">{detalle.direccionEntrega || detalle.DireccionEntrega}</Typography>
                {(detalle.instrucciones || detalle.Instrucciones) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {detalle.instrucciones || detalle.Instrucciones}
                  </Typography>
                )}
                {(detalle.latitudEntrega || detalle.LatitudEntrega) && (
                  <Button
                    size="small" startIcon={<DirectionsIcon />}
                    sx={{ mt: 1 }}
                    href={`https://www.google.com/maps/dir/?api=1&destination=${detalle.latitudEntrega || detalle.LatitudEntrega},${detalle.longitudEntrega || detalle.LongitudEntrega}`}
                    target="_blank" rel="noopener"
                  >
                    Cómo llegar
                  </Button>
                )}
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary">Método de pago</Typography>
                <Typography variant="body2">{detalle.metodoPago || detalle.MetodoPago}</Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="overline" color="text.secondary">Productos</Typography>
                {(detalle.items || detalle.Items || []).map((it, i) => (
                  <Box key={i} sx={{ py: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      {it.cantidad || it.Cantidad}× {it.nombre || it.Nombre}
                    </Typography>
                    <Typography variant="body2">{formatoCOP(it.subtotal || it.Subtotal)}</Typography>
                  </Box>
                ))}
              </Box>

              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>
                  {formatoCOP(detalle.total || detalle.Total)}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          {['preparacion','en_ruta'].includes(detalle?.estado || detalle?.Estado) && (
            <Button
              color="error" variant="outlined" startIcon={<WarningIcon />}
              onClick={() => setSos({ pedidoId: detalle.pedidoId || detalle.PedidoId, mensaje: '' })}
            >
              SOS
            </Button>
          )}
          {(detalle?.estado === 'preparacion' || detalle?.Estado === 'preparacion') && (
            <Button
              variant="contained" startIcon={<LocalShippingIcon />}
              onClick={() => cambiarEstado(detalle.pedidoId || detalle.PedidoId, 'en_ruta')}
            >
              Recogí, voy en ruta
            </Button>
          )}
          {(detalle?.estado === 'en_ruta' || detalle?.Estado === 'en_ruta') && (
            <Button
              variant="contained" color="success" startIcon={<CheckCircleIcon />}
              onClick={() => cambiarEstado(detalle.pedidoId || detalle.PedidoId, 'entregado')}
            >
              Entregado
            </Button>
          )}
          <Button onClick={() => setDetalle(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog SOS */}
      <Dialog open={!!sos} onClose={() => setSos(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ color: 'error.main' }}>Enviar alerta SOS</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Se enviará tu ubicación GPS al negocio y quedará registro del evento.
          </Alert>
          <TextField
            fullWidth multiline minRows={2}
            label="¿Qué pasa? (opcional)"
            value={sos?.mensaje || ''}
            onChange={(e) => setSos(s => ({ ...s, mensaje: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSos(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={dispararSOS}>
            Enviar SOS
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
